import React from "react";
import { TeamsHook, useTeamsHooksForTenantQuery } from "generated/graphql";
import { Link, Route, RouteComponentProps, Switch } from "polyrouter";
import { usePolyPlusEnabled } from "utilities/polyPlus";
import { Layout } from "views/Main/Layouts";
import { HelpLink, Loader, ToolTip } from "elements";
import { PageTitle } from "../PageTitle";
import { PolyPlusInactiveModal } from "elements/PolyPlus/PolyPlus";
import { TableBody } from "collections/Table";
import { generatePath, useRouteMatch } from "react-router";
import { WebHookForm } from "./Edit/WebHookEditForm";
import CreateTeamsHook from "./Create/CreateTeamsHook";
import { Trans, useTranslation } from "utilities/i18n";
import { DeleteTenantWebhook } from "./DeleteTenantWebhook";
import { TestFormLinkStyle } from "./Edit/TestForm";
import { DOCS } from "Constants";

function useTeamsNotificationsConfiguration(id: string) {
  const { polyPlusEnabled, fetching: polyPlusFetching } = usePolyPlusEnabled(
    id
  );
  const { fetching, ...rest } = useTeamsHooksForTenantQuery({
    variables: {
      id
    },
    pause: !polyPlusEnabled ?? true
  });
  return {
    fetching: fetching || polyPlusFetching,
    ...rest
  };
}

const WebHookTable: React.FC<{
  tenantId: string;
  basePath: string;
  handleScroll: () => void;
  hooks: TeamsHook[];
}> = ({ tenantId, basePath, handleScroll, hooks }) => {
  const { t } = useTranslation();
  const match = useRouteMatch<{ webHooksId: string }>([
    `${basePath}/:webHooksId`,
    basePath
  ]);
  const root = generatePath(basePath, { id: tenantId });
  if (!hooks.length) {
    return null;
  }

  return (
    <table className="table custom-table">
      <thead>
        <tr>
          <th className="table-header">{t("NAME")}</th>
          <th className="table-header">{t("URL")}</th>
          <th className="table-header" />
          <th className="table-header" />
        </tr>
      </thead>
      <TableBody cols={100} loading={false}>
        {hooks.map(({ name, url, id }) => (
          <tr
            key={id}
            className={
              match?.params?.webHooksId === id
                ? "checked"
                : `${match?.params?.webHooksId} ${id}`
            }
          >
            <td>
              <Link onClick={handleScroll} to={`${root}/${id}/edit`}>
                {name}
              </Link>
            </td>
            <td className="tooltip-text-cell">
              <ToolTip
                icon=""
                iconPosition="right"
                position="bottom"
                iconTitle=""
                hideTextOverflow={true}
                iconReplacement={url}
              >
                <span>{url}</span>
              </ToolTip>
            </td>
            <td>
              <TestFormLinkStyle webHooksId={id} />
            </td>
            <td>
              <Link
                to={generatePath(`${root}/:webHooksId/delete`, {
                  webHooksId: id
                })}
                className="link-delete"
              >
                {t("DELETE")}
              </Link>
            </td>
          </tr>
        ))}
      </TableBody>
    </table>
  );
};

const TeamsNotificationsConfiguration: React.FC<Pick<
  RouteComponentProps<{
    id: string;
  }>,
  "match"
>> = ({
  match: {
    params: { id },
    path
  }
}) => {
  const { fetching } = useTeamsNotificationsConfiguration(id);
  const { t } = useTranslation();
  const { data } = useTeamsNotificationsConfiguration(id);
  const hooks = data?.tenant.teamsWebhooks ?? [];
  const { Content } = Layout;

  const handleScroll = () => {
    let el = document.getElementsByClassName("scrollable")[0];
    let el2 = document.getElementById("title");
    let dimentions = el2?.getBoundingClientRect();
    if (dimentions == null) return;
    // 50 refers to the top margin, it doesn't matter whether this value is
    // descreased since we are trying to go to the very top, as long as the margin is less than 50
    // this magic number is correct which currently the element has a top margin of 20
    el?.scrollTo({
      left: el.scrollTop,
      top: dimentions.top - 50,
      behavior: "smooth"
    });
  };

  return (
    <Content>
      <Content.ScrollingList>
        <div className="notification-center-content">
          <PageTitle
            titleKey="NOTIFICATION_POLY_TITLE"
            subTitleKey="NOTIFICATION_TEAMS_TITLE"
          />
          <Route
            nest
            path="/:webHooksId/delete"
            component={DeleteTenantWebhook}
          />
          <div className="options-section">
            {/* https://react.i18next.com/latest/trans-component */}
            {/* Component idx 0 must be a p tag, idx 1 must be the a tag.*/}
            <Trans
              datatest-id="description-block"
              i18nKey="NOTIFICATION_TEAMS_HELP_INTERVAL"
            >
              <p>
                Description: To learn more, click
                <HelpLink location={DOCS.NOTIFICATION_OVERVIEW}>here</HelpLink>
              </p>
            </Trans>
          </div>
          <Loader loading={fetching}>
            <Switch nest>
              <Route
                path="/:webHooksId/edit"
                render={props => (
                  <WebHookForm
                    webHooksId={props.match.params.webHooksId}
                    hooks={hooks}
                  />
                )}
              />
              <Route path="/create" component={CreateTeamsHook} />
              <Route>
                <div className="options-section">
                  <div className="grouping table-actions mb-1">
                    <Link
                      nest
                      to="/create"
                      className="btn btn-primary"
                      data-testid="add-teams-hook"
                    >
                      {t("ADD_NEW")}
                    </Link>
                  </div>
                </div>
              </Route>
            </Switch>
            <Switch nest>
              <Route path="/create" exact={false} />
              <Route>
                <WebHookTable
                  tenantId={id}
                  basePath={path}
                  handleScroll={handleScroll}
                  hooks={hooks}
                />
              </Route>
            </Switch>
          </Loader>
        </div>
      </Content.ScrollingList>
      {!fetching && <PolyPlusInactiveModal tenantId={id} />}
    </Content>
  );
};

export default TeamsNotificationsConfiguration;
